import React, { useState, useEffect } from 'react';
import AdminHeader from '../components/AdminHeader';
import Select from 'react-select';
import axios from 'axios';
import './customStyles.css';
import cross from '../images/ImageUpload/cross.png';
import upload from '../images/ImageUpload/upload.png';
import imageCompression from 'browser-image-compression';
import { toast } from 'react-toastify';

function ImageUpload() {
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [items, setItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState('');
    const [isLoadingItems, setIsLoadingItems] = useState(false); // NEW loading state
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const apiLocalUrl = process.env.REACT_APP_API_LOCAL_URL;

    const [modifiedColors, setModifiedColors] = useState({});
    const [fabricDetails, setFabricDetails] = useState({});
    const [savingItems, setSavingItems] = useState({}); // Track individual saving states
    const [savingFabric, setSavingFabric] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ open: false, type: '', payload: null });
    const [activeTab, setActiveTab] = useState('products');
    const [banners, setBanners] = useState([]);

    const fetchBanners = () => {
        if (!apiBaseUrl) return;
        fetch(`${apiBaseUrl}/getBanners/BLACKBATON_2526`)
            .then(response => response.json())
            .then(data => setBanners(data))
            .catch(err => console.error("Error fetching banners", err));
    };

    const handleBannerImageUpload = (e, bannerName) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = async () => {
            const base64String = reader.result.replace(/^data:image\/(png|jpeg|jpg|webp);base64,/, '');
            try {
                const response = await fetch(`${apiBaseUrl}/updateBannerImage/BLACKBATON_2526`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ bannerName, base64Image: base64String })
                });
                if (response.ok) {
                    toast.success('Banner updated successfully');
                    fetchBanners();
                } else {
                    toast.error('Failed to update banner');
                }
            } catch (err) {
                console.error(err);
                toast.error('Error updating banner');
            }
        };
        reader.readAsDataURL(file);
    };

    useEffect(() => {
        const fetchModifiedColors = async () => {
            try {
                const response = await fetch(`${apiBaseUrl}/getAllModifiedColors/BLACKBATON_2526`);
                const data = await response.json();
                const modifiedColors = data.reduce((acc, item) => {
                    acc[item.db_color] = {
                        name: item.md_color?.toUpperCase() || '',
                        hex: item.hex || ''
                    };
                    return acc;
                }, {});

                setModifiedColors(modifiedColors);
            } catch (error) {
                console.error('Error fetching color and size data:', error);
            }
        };

        fetchModifiedColors();
    }, []);

    useEffect(() => {
        fetch(`${apiBaseUrl}/getCategories/BLACKBATON_2526`)
            .then(response => response.json())
            .then(data => setCategories(data));
        fetchBanners();
    }, [apiBaseUrl]);

    useEffect(() => {
        if (selectedCategory) {
            // Fetch Products using Optimized Lightweight Endpoint (No Pagination)
            setIsLoadingItems(true);
            fetch(`${apiBaseUrl}/getProductListByCategory/BLACKBATON_2526?Id=${selectedCategory.value}`)
                .then(response => response.json())
                .then(data => {
                    const productsList = Array.isArray(data) ? data : (data.products || []);
                    setItems(productsList.map(item => ({
                        Id: item.ID,
                        ItemName: item.ItemName,
                        displayFirst: item.displayFirst,
                    })));
                })
                .catch(err => console.error("Error fetching items:", err))
                .finally(() => setIsLoadingItems(false));
        }
    }, [selectedCategory, apiBaseUrl]);

    const handleCategoryChange = async (selectedCategory) => {
        setSelectedCategory(selectedCategory);
        setSelectedItem('');
        setEachItem([]);
        setAllExtraImages({});
        setImagesToDelete([]);
    };

    const [eachItem, setEachItem] = useState([]);
    const [displayFirstCustom, setDisplayFirstCustom] = useState('');
    const [noSize, setNoSize] = useState(null);

    const fetchProduct = async (itemId) => {
        try {
            const response = await fetch(`${apiBaseUrl}/getProductById/BLACKBATON_2526?Id=${itemId}`);
            const data = await response.json();
            if (data.length > 0) {
                setNoSize(data[0].noSize);
            }
        } catch (error) {
            console.error('Error fetching product:', error);
        }
    };

    // Combined state for all extra images (both fetched and new)
    const [allExtraImages, setAllExtraImages] = useState({});
    // Track images to delete from server
    const [imagesToDelete, setImagesToDelete] = useState([]);

    const handleGetItems = async (itemId) => {
        try {
            const response = await axios.get(`${apiBaseUrl}/getProductDetails/BLACKBATON_2526?Id=${itemId}`);

            if (response.status === 200) {
                fetchProduct(itemId);

                const loadingStatus = {};
                const seen = new Set();

                response.data.forEach(({ ItemId, Color }) => {
                    const color = modifiedColors[Color?.toUpperCase()]?.name;
                    const key = `${ItemId}_${color}`;
                    if (!seen.has(key)) {
                        seen.add(key);
                        loadingStatus[key] = true;
                    }
                });

                setLoadingPreviews(loadingStatus);
                setEachItem(response.data);

                // Fetch product-specific fabric details
                fetchFabricData(itemId);

                // Fetch main images
                fetchAllImageBlobs(seen);

                // Also fetch extra images for each key
                fetchAllExtraImages(seen);

                const selectedItem = items.find(item => item.Id === itemId);
                if (selectedItem) {
                    setDisplayFirstCustom(selectedItem.displayFirst);
                    setDisplayFirst(selectedItem.displayFirst);
                }
            } else {
                toast.info('Failed to fetch product details.');
            }
        } catch (error) {
            console.error('Error fetching items:', error);
            toast.info('Error fetching product details.');
        }
    };

    const handleItemChange = (selectedItem) => {

        if (selectedItem) {
            setSelectedItem(selectedItem.value);
            handleGetItems(selectedItem.value);
        } else {
            setSelectedItem('');
            setEachItem([]);
            setAllExtraImages({});
            setImagesToDelete([]);
            setPreviews({}); // Clear previews on item change
        }
    };

    const [displayFirst, setDisplayFirst] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState('idle');
    const [imageUploadingProgress, setImageUploadingProgress] = useState(0);

    const handleSaveImages = async () => {
        // Check if there are any changes
        const hasMainImages = Object.keys(images).length > 0;
        const hasDisplayFirstChange = displayFirstCustom !== displayFirst;

        // Check if there are any NEW extra images to upload
        let hasNewExtraImages = false;
        Object.keys(allExtraImages).forEach(key => {
            if (allExtraImages[key].some(img => img.type === 'new')) {
                hasNewExtraImages = true;
            }
        });

        const hasImagesToDelete = imagesToDelete.length > 0;

        // Check for fabric changes
        let hasFabricChanges = false;
        const detail = fabricDetails[selectedItem];
        if (detail && (detail.image || detail.gsm || detail.dryingSpeed || detail.cooling || detail.breathability || detail.stretchability)) {
            hasFabricChanges = true;
        }

        if (!hasMainImages && !hasNewExtraImages && !hasDisplayFirstChange && !hasImagesToDelete && !hasFabricChanges) {
            console.warn("No changes to save");
            return;
        }

        const payload = {
            slno: selectedItem,
            displayFirst: displayFirst
        };

        setIsUploading(true);
        setUploadStatus('saving');

        // Prepare form data for main images
        let mainFormData = null;
        let completed = 0;
        let totalOperations = 0;

        if (hasMainImages) {
            mainFormData = new FormData();
            totalOperations += Object.keys(images).length;
        }

        // Prepare form data for extra images
        let extraFormData = null;
        if (hasNewExtraImages) {
            extraFormData = new FormData();
            Object.keys(allExtraImages).forEach(key => {
                const newImages = allExtraImages[key].filter(img => img.type === 'new');
                totalOperations += newImages.length;
            });
        }

        // Add delete operations
        if (hasImagesToDelete) {
            totalOperations += imagesToDelete.length;
        }

        const compressionOptions = {
            maxSizeMB: 1,
            useWebWorker: true,
            fileType: 'image/webp',
            initialQuality: 1
        };

        // Delete images from server
        if (hasImagesToDelete) {
            try {
                const deleteResponse = await fetch(`${apiLocalUrl}/delete-images`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ images: imagesToDelete })
                });

                if (!deleteResponse.ok) {
                    throw new Error('Failed to delete images');
                }

                completed += imagesToDelete.length;
                const percent = Math.round((completed / totalOperations) * 100);
                setImageUploadingProgress(percent);
            } catch (error) {
                console.error('Error deleting images:', error);
            }
        }

        // Upload main images
        if (hasMainImages) {
            for (const [key, file] of Object.entries(images)) {
                try {
                    const compressedFile = await imageCompression(file, compressionOptions);
                    const renamedFile = new File([compressedFile], `${key}.webp`, {
                        type: 'image/webp',
                    });
                    mainFormData.append('images', renamedFile);

                    completed++;
                    const percent = Math.round((completed / totalOperations) * 100);
                    setImageUploadingProgress(percent);
                } catch (err) {
                    console.error(`Compression error on [${key}]:`, err);
                }
            }

            try {
                const response = await fetch(`${apiLocalUrl}/upload-image1`, {
                    method: 'POST',
                    body: mainFormData,
                });

                if (!response.ok) {
                    throw new Error('Failed to upload main images');
                }
            } catch (error) {
                console.error('Error uploading main images:', error);
            }
        }

        // Upload NEW extra images (skip fetched ones)
        if (hasNewExtraImages) {
            for (const [baseKey, imagesArray] of Object.entries(allExtraImages)) {
                let newImageIndex = 0;

                // Find the highest existing index for this baseKey
                const existingIndices = imagesArray
                    .filter(img => img.type === 'fetched')
                    .map(img => {
                        // Extract index from filename like "1431_BLUE_1.webp"
                        const match = img.filename.match(/_(\d+)\.webp$/);
                        return match ? parseInt(match[1]) : 0;
                    })
                    .filter(num => !isNaN(num));

                const startIndex = existingIndices.length > 0 ? Math.max(...existingIndices) + 1 : 1;

                for (let i = 0; i < imagesArray.length; i++) {
                    const item = imagesArray[i];
                    if (item.type === 'new') {
                        try {
                            const compressedFile = await imageCompression(item.file, compressionOptions);
                            const extraKey = `${baseKey}_${startIndex + newImageIndex}`;
                            newImageIndex++;

                            const renamedFile = new File([compressedFile], `${extraKey}.webp`, {
                                type: 'image/webp',
                            });
                            extraFormData.append('extraImages', renamedFile);

                            completed++;
                            const percent = Math.round((completed / totalOperations) * 100);
                            setImageUploadingProgress(percent);
                        } catch (err) {
                            console.error(`Compression error on extra image [${baseKey}_${i + 1}]:`, err);
                        }
                    }
                }
            }

            if (extraFormData) {
                try {
                    const response = await fetch(`${apiLocalUrl}/upload-extra-images`, {
                        method: 'POST',
                        body: extraFormData,
                    });

                    if (!response.ok) {
                        throw new Error('Failed to upload extra images');
                    }
                } catch (error) {
                    console.error('Error uploading extra images:', error);
                }
            }
        }

        // Update displayFirst if changed
        if (hasDisplayFirstChange) {
            try {
                const response = await fetch(`${apiBaseUrl}/postDisplayFirst/BLACKBATON_2526`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    throw new Error('Failed to update display first');
                }
            } catch (error) {
                console.error('Error updating display first:', error);
            }
        }

        // Upload Fabric Data
        if (hasFabricChanges) {
            const details = fabricDetails[selectedItem];
            try {
                const formData = new FormData();
                formData.append('code', selectedItem);
                formData.append('gsm', details.gsm || '');
                formData.append('dryingSpeed', details.dryingSpeed || 0);
                formData.append('cooling', details.cooling || 0);
                formData.append('breathability', details.breathability || 0);
                formData.append('stretchability', details.stretchability || 0);

                if (details.image) {
                    const compressedFile = await imageCompression(details.image, compressionOptions);
                    formData.append('fabricImage', compressedFile);
                }

                const response = await fetch(`${apiLocalUrl}/upload-fabric-data/BLACKBATON_2526`, {
                    method: 'POST',
                    body: formData,
                });

                if (!response.ok) {
                    throw new Error(`Failed to upload fabric data for product ${selectedItem}`);
                }
            } catch (error) {
                console.error(`Error saving fabric data for product ${selectedItem}:`, error);
            }
        }

        setUploadStatus('success');
        setImageUploadingProgress(100);

        setTimeout(() => {
            setImages({});
            setPreviews({});
            setAllExtraImages({});
            setFabricDetails({});
            setImagesToDelete([]);
            setSelectedCategory('');
            setSelectedItem('');
            setEachItem([]);
            setDisplayFirst('');
            setDisplayFirstCustom('');
            setIsUploading(false);
            setUploadStatus('idle');
        }, 1000);
    };

    const handleSaveFabric = async () => {
        if (!selectedItem) return;

        const details = fabricDetails[selectedItem];
        if (!details) {
            console.warn("No fabric details to save.");
            return;
        }

        setSavingFabric(true);
        const compressionOptions = {
            maxSizeMB: 1,
            useWebWorker: true,
            fileType: 'image/webp',
            initialQuality: 1
        };

        try {
            const formData = new FormData();
            formData.append('code', selectedItem);
            formData.append('gsm', details.gsm || '');
            formData.append('dryingSpeed', details.dryingSpeed || 0);
            formData.append('cooling', details.cooling || 0);
            formData.append('breathability', details.breathability || 0);
            formData.append('stretchability', details.stretchability || 0);

            if (details.image) {
                const compressedFile = await imageCompression(details.image, compressionOptions);
                formData.append('fabricImage', compressedFile);
            }

            const response = await fetch(`${apiLocalUrl}/upload-fabric-data/BLACKBATON_2526`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`Failed to upload fabric data for product ${selectedItem}`);
            }

            setSavingFabric('success');
            setTimeout(() => {
                setSavingFabric(false);
                fetchFabricData(selectedItem);
            }, 1000);

        } catch (error) {
            console.error(`Error saving fabric data for product ${selectedItem}:`, error);
            setSavingFabric('error');
            setTimeout(() => setSavingFabric(false), 2000);
        }
    };

    const handleSingleSave = async (code) => {
        const itemImages = images[code];
        const itemExtraImages = allExtraImages[code] || [];
        const hasNewExtraImages = itemExtraImages.some(img => img.type === 'new');

        // Filter images to delete for this specific code
        const itemImagesToDelete = imagesToDelete.filter(filename => {
            return filename === `${code}.webp` || filename.startsWith(`${code}_`);
        });

        if (!itemImages && !hasNewExtraImages && itemImagesToDelete.length === 0) {
            console.warn(`No changes to save for ${code}`);
            return;
        }

        setSavingItems(prev => ({ ...prev, [code]: true }));

        const compressionOptions = {
            maxSizeMB: 1,
            useWebWorker: true,
            fileType: 'image/webp',
            initialQuality: 1
        };

        try {
            // 1. Delete images
            if (itemImagesToDelete.length > 0) {
                await fetch(`${apiLocalUrl}/delete-images`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ images: itemImagesToDelete })
                });
                setImagesToDelete(prev => prev.filter(f => !itemImagesToDelete.includes(f)));
            }

            // 2. Upload main image
            if (itemImages) {
                const mainFormData = new FormData();
                const compressedFile = await imageCompression(itemImages, compressionOptions);
                const renamedFile = new File([compressedFile], `${code}.webp`, { type: 'image/webp' });
                mainFormData.append('images', renamedFile);

                await fetch(`${apiLocalUrl}/upload-image1`, {
                    method: 'POST',
                    body: mainFormData,
                });
                setImages(prev => {
                    const next = { ...prev };
                    delete next[code];
                    return next;
                });
            }

            // 3. Upload NEW extra images
            if (hasNewExtraImages) {
                const extraFormData = new FormData();
                const newImages = itemExtraImages.filter(img => img.type === 'new');

                const existingIndices = itemExtraImages
                    .filter(img => img.type === 'fetched')
                    .map(img => {
                        const match = img.filename.match(/_(\d+)\.webp$/);
                        return match ? parseInt(match[1]) : 0;
                    })
                    .filter(num => !isNaN(num));

                const startIndex = existingIndices.length > 0 ? Math.max(...existingIndices) + 1 : 1;

                for (let i = 0; i < newImages.length; i++) {
                    const compressedFile = await imageCompression(newImages[i].file, compressionOptions);
                    const extraKey = `${code}_${startIndex + i}`;
                    const renamedFile = new File([compressedFile], `${extraKey}.webp`, { type: 'image/webp' });
                    extraFormData.append('extraImages', renamedFile);
                }

                await fetch(`${apiLocalUrl}/upload-extra-images`, {
                    method: 'POST',
                    body: extraFormData,
                });

                // Update items to 'fetched'
                setAllExtraImages(prev => ({
                    ...prev,
                    [code]: prev[code].map(img => img.type === 'new' ? { ...img, type: 'fetched', filename: `${code}_${startIndex + newImages.indexOf(img)}.webp` } : img)
                }));
            }

            setSavingItems(prev => ({ ...prev, [code]: 'success' }));
            setTimeout(() => {
                setSavingItems(prev => ({ ...prev, [code]: false }));
            }, 1000);

        } catch (error) {
            console.error(`Error saving ${code}:`, error);
            setSavingItems(prev => ({ ...prev, [code]: 'error' }));
            setTimeout(() => setSavingItems(prev => ({ ...prev, [code]: false })), 2000);
        }
    };

    const [images, setImages] = useState({});
    const [previews, setPreviews] = useState({});
    const [loadingPreviews, setLoadingPreviews] = useState({});

    const handleImageChange = (e, code) => {
        const file = e.target.files[0];
        if (file) {
            const newImages = { ...images, [code]: file };
            const newPreviews = { ...previews, [code]: URL.createObjectURL(file) };
            setImages(newImages);
            setPreviews(newPreviews);
        }
    };

    // Function to handle extra image upload
    const handleExtraImageChange = (e, baseCode) => {
        const file = e.target.files[0];
        if (file) {
            const previewUrl = URL.createObjectURL(file);

            setAllExtraImages(prev => {
                const current = prev[baseCode] || [];
                return {
                    ...prev,
                    [baseCode]: [
                        ...current,
                        {
                            type: 'new',
                            file,
                            preview: previewUrl
                        }
                    ]
                };
            });

            // Reset the file input to allow selecting the same file again
            e.target.value = '';
        }
    };

    // Function to remove main image
    const executeRemoveMainImage = (code) => {
        // If it's a fetched image (already on server), add to delete list
        if (previews[code] && !images[code]) {
            // This is a fetched image, mark it for deletion
            setImagesToDelete(prev => [...prev, `${code}.webp`]);
        }

        // Remove from state
        const newImages = { ...images };
        const newPreviews = { ...previews };
        const newLoading = { ...loadingPreviews };
        delete newImages[code];
        delete newPreviews[code];
        delete newLoading[code];
        setImages(newImages);
        setPreviews(newPreviews);
        setLoadingPreviews(newLoading);
    };

    // Function to remove extra image
    const executeRemoveExtraImage = (baseCode, index) => {
        setAllExtraImages(prev => {
            if (!prev[baseCode]) return prev;

            const newArray = [...prev[baseCode]];
            const item = newArray[index];

            // If it's a fetched image, add to delete list
            if (item.type === 'fetched') {
                setImagesToDelete(prevDeletes => [...prevDeletes, item.filename]);
            }

            // Revoke object URL to prevent memory leak
            if (item.preview && item.type === 'new') {
                URL.revokeObjectURL(item.preview);
            }

            newArray.splice(index, 1);

            const newState = { ...prev };
            if (newArray.length === 0) {
                delete newState[baseCode];
            } else {
                newState[baseCode] = newArray;
            }

            return newState;
        });
    };

    const removeMainImage = (code) => {
        setConfirmModal({ open: true, type: 'main', payload: { code } });
    };

    const removeExtraImage = (baseCode, index) => {
        setConfirmModal({ open: true, type: 'extra', payload: { baseCode, index } });
    };

    const handleConfirmAction = () => {
        if (confirmModal.type === 'main' && confirmModal.payload?.code) {
            executeRemoveMainImage(confirmModal.payload.code);
        } else if (confirmModal.type === 'extra' && confirmModal.payload) {
            executeRemoveExtraImage(confirmModal.payload.baseCode, confirmModal.payload.index);
        }
        setConfirmModal({ open: false, type: '', payload: null });
    };

    const handleImageError = (code) => {
        setLoadingPreviews(prev => ({ ...prev, [code]: false }));
    };

    const fetchAllImageBlobs = async (seen) => {
        try {
            const response = await axios.get(`${apiLocalUrl}/getImages`);

            // Backend returns a simple array of strings: ["1431_BLUE.webp", ...]
            let filenames = [];
            if (Array.isArray(response.data)) {
                filenames = response.data.map(item => typeof item === 'string' ? item : item.name);
            }

            const newPreviews = {};
            const newLoading = { ...loadingPreviews };

            filenames.forEach(filename => {
                if (!filename || typeof filename !== 'string') return;

                const imageKey = filename.replace('.webp', '');
                // Check if it's a main image (not an extra image with number suffix)
                // Main images follow pattern "ID_COLOR" while extra follow "ID_COLOR_INDEX"
                const parts = imageKey.split('_');

                // If parts.length > 2, it might be an extra image, e.g., "1431_BLUE_1"
                // However, some colors might have underscores in their names? 
                // Let's check the last part for numeric value.
                const lastPart = parts[parts.length - 1];
                if (isNaN(lastPart)) {
                    // This is likely a main image
                    // Check if it's one of the items we're currently looking at
                    newPreviews[imageKey] = `${apiLocalUrl}/uploads/${filename}`;
                    newLoading[imageKey] = false;
                }
            });

            setPreviews(prev => ({ ...prev, ...newPreviews }));
            setLoadingPreviews(newLoading);

            seen.forEach((key) => {
                if (!newPreviews[key]) {
                    setLoadingPreviews(prev => ({ ...prev, [key]: false }));
                }
            });

        } catch (err) {
            console.error('Error fetching image list:', err);
            setLoadingPreviews({});

            // Set all seen keys to false loading state
            if (seen) {
                const updatedLoading = { ...loadingPreviews };
                seen.forEach(key => {
                    updatedLoading[key] = false;
                });
                setLoadingPreviews(updatedLoading);
            }
        }
    };

    const fetchAllExtraImages = async (seen) => {
        try {
            const response = await axios.get(`${apiLocalUrl}/getExtraImages`);

            // Backend returns a simple array of strings: ["1431_BLUE_1.webp", ...]
            let filenames = [];
            if (Array.isArray(response.data)) {
                filenames = response.data.map(item => typeof item === 'string' ? item : item.name);
            }

            const newAllExtraImages = {};

            filenames.forEach(filename => {
                if (!filename || typeof filename !== 'string') return;

                const imageKey = filename.replace('.webp', '');
                const lastUnderscore = imageKey.lastIndexOf('_');
                if (lastUnderscore === -1) return;

                const baseKey = imageKey.substring(0, lastUnderscore);

                if (!newAllExtraImages[baseKey]) {
                    newAllExtraImages[baseKey] = [];
                }

                newAllExtraImages[baseKey].push({
                    type: 'fetched',
                    preview: `${apiLocalUrl}/uploads/subImages/${filename}`,
                    filename: filename
                });
            });

            setAllExtraImages(prev => {
                const merged = { ...prev };
                Object.keys(newAllExtraImages).forEach(baseKey => {
                    if (merged[baseKey]) {
                        const existingNewImages = merged[baseKey].filter(img => img.type === 'new');
                        merged[baseKey] = [...newAllExtraImages[baseKey], ...existingNewImages];
                    } else {
                        merged[baseKey] = newAllExtraImages[baseKey];
                    }
                });
                return merged;
            });

        } catch (err) {
            console.error('Error fetching extra images:', err);
        }
    };

    const fetchFabricData = async (code) => {
        try {
            const response = await axios.get(`${apiLocalUrl}/get-fabric-data/${code}/BLACKBATON_2526`);
            if (response.data.success) {
                setFabricDetails(prev => ({
                    ...prev,
                    [code]: {
                        ...response.data.data,
                        fetchedImage: response.data.data.hasImage ? `${apiLocalUrl}/uploads/fabric/${code}.webp` : null
                    }
                }));
            }
        } catch (error) {
            console.error("Error fetching fabric data:", error);
        }
    };

    const handleFabricChange = (code, field, value) => {
        setFabricDetails(prev => ({
            ...prev,
            [code]: {
                ...(prev[code] || { gsm: '', dryingSpeed: 0, cooling: 0, breathability: 0, stretchability: 0 }),
                [field]: value
            }
        }));
    };

    const handleFabricImageChange = (e, code) => {
        const file = e.target.files[0];
        if (file) {
            const previewUrl = URL.createObjectURL(file);
            setFabricDetails(prev => ({
                ...prev,
                [code]: {
                    ...(prev[code] || { gsm: '', dryingSpeed: 0, cooling: 0, breathability: 0, stretchability: 0 }),
                    image: file,
                    preview: previewUrl
                }
            }));
        }
    };

    const RatingSelector = ({ label, value, onChange }) => (
        <div className="space-y-1">
            <label className="text-[10px] text-left uppercase font-bold text-gray-500 block">{label}</label>
            <div className="flex gap-1.5">
                {[1, 2, 3, 4, 5].map((num) => (
                    <button
                        key={num}
                        type="button"
                        onClick={() => onChange(num)}
                        className={`w-3.5 h-3.5 rounded-full border transition-all ${value >= num ? 'bg-black border-black scale-110' : 'bg-gray-100 border-gray-200 hover:border-gray-400'
                            }`}
                    />
                ))}
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-dmSans">
            <AdminHeader />

            <div className="flex-grow py-12 lg:px-12 px-4 shadow-inner">
                <div className="max-w-full mx-auto bg-white shadow-2xl rounded-2xl border border-gray-100 p-8">
                    {/* <header className="mb-10 text-center">
                        <h1 className="text-4xl font-extrabold tracking-tight text-black mb-2">Image Upload</h1>
                        <div className="w-20 h-1 bg-black mx-auto mt-4 rounded-full"></div>
                    </header> */}

                    <div className="flex justify-center mb-8">
                        <div className="inline-flex bg-gray-100 rounded-full p-1">
                            <button
                                className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${activeTab === 'products' ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-black'}`}
                                onClick={() => setActiveTab('products')}
                            >
                                Product Images
                            </button>
                            <button
                                className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${activeTab === 'banners' ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-black'}`}
                                onClick={() => setActiveTab('banners')}
                            >
                                Banners
                            </button>
                        </div>
                    </div>

                    {activeTab === 'products' && (
                        <>
                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${eachItem.length > 0 && ' mb-6'}`}>
                        <div className="space-y-2 text-left">
                            <label className="text-xs uppercase tracking-widest font-bold text-gray-500 ml-1">Category</label>
                            <Select
                                value={selectedCategory}
                                onChange={handleCategoryChange}
                                options={categories.map(category => ({ value: category.Id, label: category.Name }))}
                                className="premium-select"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderRadius: '12px',
                                        padding: '4px',
                                        border: '1px solid #e5e7eb',
                                        boxShadow: 'none',
                                        '&:hover': { border: '1px solid #000' }
                                    })
                                }}
                                isClearable
                                isSearchable
                                placeholder="Select a category..."
                            />
                        </div>

                        {selectedCategory && (
                            <div className="space-y-2 text-left">
                                <label className="text-xs uppercase tracking-widest font-bold text-gray-500 ml-1">Product Item</label>
                                <Select
                                    onChange={handleItemChange}
                                    value={selectedItem ? { value: selectedItem, label: items.find(item => item.Id === selectedItem)?.ItemName } : null}
                                    options={items.map(item => ({ value: item.Id, label: item.ItemName }))}
                                    className="premium-select"
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderRadius: '12px',
                                            padding: '4px',
                                            border: '1px solid #e5e7eb',
                                            boxShadow: 'none',
                                            '&:hover': { border: '1px solid #000' }
                                        })
                                    }}
                                    isClearable
                                    isSearchable
                                    placeholder={isLoadingItems ? "Loading products..." : "Select an item..."}
                                    isLoading={isLoadingItems}
                                />
                            </div>
                        )}
                    </div>

                    {/* Product-Specific Fabric Details Section */}
                    {selectedItem && (
                        <div className="mb-12">
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                <div className="bg-gray-50/50 px-8 py-4 border-b border-gray-100 flex justify-between items-center">
                                    <h3 className="text-sm font-black text-black uppercase tracking-widest flex items-center gap-2">
                                        <div className="w-1.5 h-4 bg-black rounded-full"></div>
                                        Fabric Details
                                        {/* <span className="text-[10px] text-gray-400 font-bold ml-2 lowercase italic">(global product data)</span> */}
                                    </h3>
                                    <button
                                        onClick={handleSaveFabric}
                                        disabled={savingFabric === true}
                                        className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 shadow-sm
                                            ${savingFabric === 'success' ? 'bg-green-500 text-white' :
                                                savingFabric === 'error' ? 'bg-red-500 text-white' :
                                                    savingFabric === true ? 'bg-gray-200 text-gray-400 cursor-not-allowed' :
                                                        'bg-black text-white hover:bg-gray-800'}`}
                                    >
                                        {savingFabric === 'success' ? 'Saved ✓' :
                                            savingFabric === 'error' ? 'Error ✗' :
                                                savingFabric === true ? 'Saving Fabric...' : 'Save Fabric'}
                                    </button>
                                </div>

                                <div className="p-8 flex flex-col lg:flex-row gap-12">
                                    {/* Primary Column: Image & GSM */}
                                    <div className="lg:w-[320px] flex flex-col gap-6 shrink-0">
                                        <div className="space-y-3">
                                            <label className="text-[10px] uppercase font-black text-gray-400 tracking-widest ml-1">Texture Preview</label>
                                            <div
                                                className="w-full aspect-square rounded-2xl border-2 border-dashed border-gray-100 flex items-center justify-center cursor-pointer hover:border-black transition-all bg-gray-50/30 group/fabric relative overflow-hidden"
                                                onClick={() => document.getElementById(`fabric-file-${selectedItem}`).click()}
                                            >
                                                {(fabricDetails[selectedItem]?.preview || fabricDetails[selectedItem]?.fetchedImage) ? (
                                                    <div className="w-full h-full relative">
                                                        <img
                                                            src={fabricDetails[selectedItem]?.preview || fabricDetails[selectedItem]?.fetchedImage}
                                                            className="w-full h-full object-cover"
                                                            alt="Fabric"
                                                        />
                                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/fabric:opacity-100 transition-opacity flex items-center justify-center">
                                                            <span className="text-[10px] text-white font-bold uppercase tracking-widest">Change Image</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center gap-2 text-gray-300 group-hover/fabric:text-black transition-colors">
                                                        <img className="w-6 opacity-20 group-hover/fabric:opacity-100 transition-opacity" src={upload} alt="Upload" />
                                                        <span className="text-[10px] font-black uppercase tracking-tighter">Upload Texture</span>
                                                    </div>
                                                )}
                                                <input
                                                    type="file"
                                                    id={`fabric-file-${selectedItem}`}
                                                    className="hidden"
                                                    onChange={(e) => handleFabricImageChange(e, selectedItem)}
                                                    accept="image/*"
                                                />
                                            </div>
                                        </div>


                                    </div>

                                    {/* Secondary Column: Ratings */}
                                    <div className="flex-grow">
                                        <label className="text-[10px] text-left uppercase font-black text-gray-400 tracking-widest ml-1 mb-4 block  ">Performance Metrics</label>
                                        <div className="space-y-3 w-1/2 mb-8">
                                            <label className="text-[10px] uppercase font-black text-left text-gray-400 tracking-widest ml-1">GSM</label>
                                            <div className="relative group/gsm">
                                                <input
                                                    type="text"
                                                    placeholder="e.g. 180"
                                                    className="w-full text-sm font-bold p-2 bg-gray-50/50 rounded-xl border border-gray-100 focus:outline-none focus:bg-white focus:border-black transition-all shadow-sm"
                                                    value={fabricDetails[selectedItem]?.gsm || ''}
                                                    onChange={(e) => handleFabricChange(selectedItem, 'gsm', e.target.value)}
                                                />
                                                {/* <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-gray-300 group-focus-within/gsm:text-black uppercase">gr/m²</span> */}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 bg-gray-50/30  rounded-3xl border border-gray-50">

                                            <div className="space-y-8">
                                                <RatingSelector
                                                    label="Drying Speed"
                                                    value={fabricDetails[selectedItem]?.dryingSpeed || 0}
                                                    onChange={(val) => handleFabricChange(selectedItem, 'dryingSpeed', val)}
                                                />
                                                <RatingSelector
                                                    label="Cooling Effect"
                                                    value={fabricDetails[selectedItem]?.cooling || 0}
                                                    onChange={(val) => handleFabricChange(selectedItem, 'cooling', val)}
                                                />
                                            </div>
                                            <div className="space-y-8">
                                                <RatingSelector
                                                    label="Breathability"
                                                    value={fabricDetails[selectedItem]?.breathability || 0}
                                                    onChange={(val) => handleFabricChange(selectedItem, 'breathability', val)}
                                                />
                                                <RatingSelector
                                                    label="Stretchability"
                                                    value={fabricDetails[selectedItem]?.stretchability || 0}
                                                    onChange={(val) => handleFabricChange(selectedItem, 'stretchability', val)}
                                                />
                                            </div>
                                        </div>
                                        {/* <p className="mt-6 text-[10px] text-gray-400 font-bold uppercase tracking-tight text-center lg:text-left">
                                            Note: These ratings will be displayed as interactive dots on the product page.
                                        </p> */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
                        {[...new Map(
                            eachItem
                                .map(innerItem => [innerItem.Color, innerItem])
                        ).values()]
                            .filter(item => modifiedColors[item.Color]?.name)
                            .reduce((acc, item) => {
                                const mappedColor = modifiedColors[item.Color]?.name;
                                if (!acc.seen.has(mappedColor)) {
                                    acc.seen.add(mappedColor);
                                    acc.result.push(item);
                                }
                                return acc;
                            }, { seen: new Set(), result: [] })
                            .result
                            .map((match, index) => {
                                const itemId = match.ItemId;
                                const colorName = modifiedColors[match.Color?.toUpperCase()]?.name;
                                const code = `${itemId}_${colorName}`;
                                const bgColor = modifiedColors[match.Color?.toUpperCase()]?.hex || '';

                                const extraImagesForCode = allExtraImages[code] || [];
                                const hasMainImage = previews[code] || images[code];

                                const firstVariantInRange = eachItem.find(v => {
                                    const vColor = modifiedColors[v.Color?.toUpperCase()]?.name || v.Color;
                                    return vColor === colorName;
                                });

                                return (
                                    <div key={index} className="bg-gray-50/50 rounded-2xl p-5 border border-gray-100 transition-all hover:bg-white hover:shadow-xl group">
                                        <div className='flex justify-between items-center mb-4'>
                                            <div className='flex items-center gap-3'>
                                                <button
                                                    onClick={() => handleSingleSave(code)}
                                                    disabled={savingItems[code] === true}
                                                    className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase transition-all active:scale-95 shadow-sm
                                                        ${savingItems[code] === 'success' ? 'bg-green-500 text-white' :
                                                            savingItems[code] === 'error' ? 'bg-red-500 text-white' :
                                                                savingItems[code] === true ? 'bg-gray-200 text-gray-400 cursor-not-allowed' :
                                                                    'bg-white text-black hover:bg-black hover:text-white border border-gray-100 hover:border-black'}`}
                                                >
                                                    {savingItems[code] === 'success' ? 'Saved ✓' :
                                                        savingItems[code] === 'error' ? 'Error ✗' :
                                                            savingItems[code] === true ? 'Saving...' : 'Save'}
                                                </button>
                                                <div
                                                    className='w-6 h-6 rounded-full border-2 border-white shadow-sm'
                                                    style={{ backgroundColor: bgColor || '#ccc' }}
                                                ></div>
                                                <span className='font-bold text-sm tracking-wide text-black'>{colorName}</span>
                                            </div>
                                            {previews[code] && (
                                                <div className='flex items-center gap-2'>
                                                    <span className="text-[10px] uppercase font-bold text-gray-400">Display</span>
                                                    <input name="productColour" className='cursor-pointer w-4 h-4 accent-black' type="radio"
                                                        checked={displayFirst === `${code}.webp`}
                                                        onChange={() => setDisplayFirst(`${code}.webp`)} />
                                                </div>
                                            )}
                                        </div>

                                        <div className='relative aspect-square mb-4'>
                                            <div
                                                className={`w-full h-full flex justify-center items-center rounded-xl border-2 border-dashed transition-all ${(!previews[code] && !loadingPreviews[code]) ? 'border-gray-200 cursor-pointer hover:border-black bg-white' : 'border-transparent'}`}
                                                onClick={() => {
                                                    if (!previews[code] && !loadingPreviews[code]) {
                                                        document.getElementById(`file-${code}`).click();
                                                    }
                                                }}
                                            >
                                                <input
                                                    type="file"
                                                    id={`file-${code}`}
                                                    className="hidden"
                                                    onChange={(e) => handleImageChange(e, code)}
                                                    accept="image/*"
                                                />
                                                {(loadingPreviews[code]) ? (
                                                    <div className="flex flex-col items-center gap-2">
                                                        <div className="w-8 h-8 border-2 border-gray-100 border-t-black rounded-full animate-spin"></div>
                                                        <span className="text-[10px] text-gray-400 font-bold uppercase">Loading</span>
                                                    </div>
                                                ) : previews[code] ? (
                                                    <div className='w-full h-full relative group/img'>
                                                        <img
                                                            className='w-full h-full object-cover rounded-xl'
                                                            src={previews[code]}
                                                            alt={colorName}
                                                        />
                                                        <button
                                                            className='absolute top-2 right-2 p-2 rounded-lg bg-black/80 text-white opacity-0 group-hover/img:opacity-100 transition-opacity backdrop-blur-sm'
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removeMainImage(code);
                                                            }}
                                                        >
                                                            <img className='w-3 invert' src={cross} alt="Remove" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className='flex flex-col justify-center items-center text-gray-400'>
                                                        <img className='w-8 opacity-20 mb-2' src={upload} alt="Upload" />
                                                        <span className='text-[10px] uppercase font-bold tracking-widest'>Click to Upload</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {hasMainImage ? (
                                            <div className="space-y-3 pt-3 border-t border-gray-100">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-[10px] uppercase font-extrabold text-gray-400 tracking-tighter">Extra-Images</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => document.getElementById(`extra-file-${code}`).click()}
                                                        className="text-[10px] font-bold bg-black text-white px-3 py-1 rounded-full hover:bg-gray-800 transition-colors uppercase tracking-tight"
                                                    >
                                                        Add Image
                                                    </button>
                                                    <input
                                                        type="file"
                                                        id={`extra-file-${code}`}
                                                        className="hidden"
                                                        onChange={(e) => handleExtraImageChange(e, code)}
                                                        accept="image/*"
                                                    />
                                                </div>

                                                <div className="flex flex-wrap gap-2">
                                                    {extraImagesForCode.map((item, idx) => (
                                                        <div key={idx} className="relative w-12 h-12 group/sub">
                                                            <img
                                                                src={item.preview}
                                                                alt={`Sub ${idx}`}
                                                                className="w-full h-full object-cover rounded-lg border border-gray-100 shadow-sm"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => removeExtraImage(code, idx)}
                                                                className="absolute -top-1 -right-1 w-5 h-5 bg-black text-white text-[10px] rounded-full flex items-center justify-center opacity-0 group-hover/sub:opacity-100 transition-opacity border-2 border-white shadow-md"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ))}
                                                    {extraImagesForCode.length === 0 && (
                                                        <div className="w-12 h-12 rounded-lg border border-gray-50 bg-gray-50/30 flex items-center justify-center">
                                                            <span className="text-[10px] text-gray-300 font-bold tracking-widest">+0</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-3 pt-3 border-t border-gray-100 opacity-40 pointer-events-none">

                                                <div className="flex justify-between items-center">
                                                    <div className="h-3 w-20 bg-gray-200 rounded"></div>
                                                    <div className="h-6 w-20 bg-gray-200 rounded-full"></div>
                                                </div>

                                                <div className="flex gap-2">
                                                    {[1, 2, 3, 4].map((_, i) => (
                                                        <div key={i} className="w-12 h-12 rounded-lg bg-gray-200 border border-gray-100"></div>
                                                    ))}
                                                </div>

                                            </div>
                                        )}


                                    </div>
                                );
                            })}
                    </div>
                    </>
                    )}

                    {activeTab === 'banners' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {[...banners, ...categories.filter(c => !banners.map(b => b.banner).includes(c.Name)).map(c => ({ banner: c.Name, imgBase64: null }))].map((b, idx) => (
                                <div key={idx} className="bg-gray-50/50 rounded-2xl p-5 border border-gray-100 transition-all hover:bg-white hover:shadow-xl flex flex-col items-center">
                                    <h3 className="font-bold text-sm tracking-wide text-black mb-4 uppercase">{b.banner}</h3>
                                    <div className='relative w-full aspect-video mb-4 rounded-xl border-2 border-dashed border-gray-200 cursor-pointer hover:border-black overflow-hidden flex justify-center items-center bg-white'
                                        onClick={() => document.getElementById(`banner-upload-${idx}`).click()}
                                    >
                                        <input
                                            type="file"
                                            id={`banner-upload-${idx}`}
                                            className="hidden"
                                            accept="image/*"
                                            onChange={(e) => handleBannerImageUpload(e, b.banner)}
                                        />
                                        {b.imgBase64 ? (
                                            <img src={`data:image/jpeg;base64,${b.imgBase64}`} className="w-full h-full object-cover" alt={b.banner} />
                                        ) : (
                                            <div className='flex flex-col justify-center items-center text-gray-400'>
                                                <img className='w-8 opacity-20 mb-2' src={upload} alt="Upload" />
                                                <span className='text-[10px] uppercase font-bold tracking-widest'>Upload Image</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {(Object.keys(images).length > 0 ||
                        Object.keys(allExtraImages).length > 0 ||
                        (displayFirstCustom !== displayFirst) ||
                        imagesToDelete.length > 0) && (
                            <div className='fixed bottom-10 left-1/2 -translate-x-1/2 z-50'>
                                <button
                                    className='py-3 px-20 bg-black text-white rounded-full font-bold shadow-2xl hover:bg-gray-900 transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:scale-100'
                                    onClick={handleSaveImages}
                                    disabled={isUploading}
                                >
                                    {isUploading ? 'Syncing...' : 'Save Changes'}
                                </button>
                            </div>
                        )}

                    {isUploading && (
                        <div className="fixed inset-0 z-[100] bg-white/90 backdrop-blur-md flex flex-col justify-center items-center transition-all">
                            {uploadStatus === 'saving' && (
                                <div className="w-full max-w-md px-10 text-center">
                                    <div className="w-24 h-24 border-4 border-gray-100 border-t-black rounded-full animate-spin mx-auto mb-8 shadow-inner"></div>
                                    <h2 className="text-2xl font-black text-black mb-4 uppercase tracking-tighter">Uploading Images</h2>
                                    <div className="w-full bg-gray-100 rounded-full h-3 mb-4 overflow-hidden shadow-inner">
                                        <div
                                            className="bg-black h-full rounded-full transition-all duration-700 ease-out"
                                            style={{ width: `${imageUploadingProgress}%` }}
                                        ></div>
                                    </div>
                                    <span className="text-4xl font-black text-black">{imageUploadingProgress}%</span>
                                </div>
                            )}
                            {uploadStatus === 'success' && (
                                <div className="text-center animate-in fade-in zoom-in duration-500">
                                    <div className="w-24 h-24 rounded-full bg-black text-white flex items-center justify-center mx-auto mb-8 shadow-2xl">
                                        <svg className="w-12 h-12" fill="none" stroke="currentColor" strokeWidth={4} viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <h2 className="text-3xl font-black text-black uppercase tracking-tighter mb-2">Upload Successful</h2>
                                    {/* <p className="text-gray-400 font-bold">Workspace updated successfully</p> */}
                                </div>
                            )}
                        </div>
                    )}
                    {confirmModal.open && (
                        <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4">
                            <div className="w-full max-w-md bg-white rounded-2xl p-6 shadow-2xl text-left">
                                <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm Delete</h3>
                                <p className="text-sm text-gray-600 mb-6">Are you sure you want to delete this image?</p>
                                <div className="flex justify-end gap-3">
                                    <button
                                        type="button"
                                        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700"
                                        onClick={() => setConfirmModal({ open: false, type: '', payload: null })}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="px-4 py-2 rounded-lg bg-black text-white"
                                        onClick={handleConfirmAction}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ImageUpload;
